import { useMemo, useState, type ReactNode } from 'react';
import type { Issue } from '../api';
import { TYPE_LABEL } from '../format';
import { HideAddressSection, RepeatSection, type ScheduleValues } from './ScheduleFields';
import { HONEYPOT_FIELD } from '../honeypot';

export const CUTOFF_COPY =
  "Submit by Wednesday at 11:59 PM to be included in Thursday morning's weekly email. Late submissions are still added to the live map.";

export interface SaleFormValues {
  event_type: string;
  title: string;
  description: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postal_code: string;
  venue_name: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  external_url: string;
  special_instructions: string;
  is_online_only: boolean;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  authorized: boolean;
  photo_rights: boolean;
  newsletter_opt_in: boolean;
  // schedule (see ScheduleFields)
  repeat: boolean;
  freq: string;
  weekdays: string;
  nth: string;
  weekday: string;
  monthday: string;
  until_date: string;
  hide_address: boolean;
  reveal_mode: string;
  reveal_date: string;
  reveal_time: string;
  // auction-only; only sent when event_type is 'auction' (see toFields)
  auction_company: string;
  auction_format: string;
  auction_start_date: string;
  auction_start_time: string;
  preview_date: string;
  preview_start_time: string;
  preview_end_time: string;
  terms_url: string;
  catalog_url: string;
  pickup_info: string;
}

const AUCTION_KEYS = [
  'auction_company',
  'auction_format',
  'auction_start_date',
  'auction_start_time',
  'preview_date',
  'preview_start_time',
  'preview_end_time',
  'terms_url',
  'catalog_url',
  'pickup_info',
] as const;

export const EMPTY_FORM: SaleFormValues = {
  event_type: 'yard',
  title: '',
  description: '',
  address_1: '',
  address_2: '',
  city: '',
  state: 'VA',
  postal_code: '',
  venue_name: '',
  start_date: '',
  start_time: '07:00',
  end_date: '',
  end_time: '13:00',
  external_url: '',
  special_instructions: '',
  is_online_only: false,
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  authorized: false,
  photo_rights: false,
  newsletter_opt_in: false,
  repeat: false,
  freq: '',
  weekdays: '',
  nth: '',
  weekday: '',
  monthday: '',
  until_date: '',
  hide_address: false,
  reveal_mode: 'evening_before',
  reveal_date: '',
  reveal_time: '',
  auction_company: '',
  auction_format: '',
  auction_start_date: '',
  auction_start_time: '',
  preview_date: '',
  preview_start_time: '',
  preview_end_time: '',
  terms_url: '',
  catalog_url: '',
  pickup_info: '',
};

/** "Times not decided yet" is stored as local 00:00 → 00:00 the next day. */
function nextDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + 1));
  return t.toISOString().slice(0, 10);
}

export function toFields(v: SaleFormValues, noTimes: boolean): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v)) out[k] = typeof val === 'boolean' ? String(val) : val;
  if (!out.end_date || v.repeat) out.end_date = out.start_date ?? '';
  if (!v.repeat) {
    for (const k of ['freq', 'weekdays', 'nth', 'weekday', 'monthday', 'until_date']) delete out[k];
  }
  if (!v.hide_address) for (const k of ['reveal_mode', 'reveal_date', 'reveal_time']) delete out[k];
  if (v.event_type !== 'auction') for (const k of AUCTION_KEYS) delete out[k];
  if (noTimes && v.start_date && !v.repeat) {
    out.start_time = '00:00';
    out.end_date = nextDay(v.start_date);
    out.end_time = '00:00';
  }
  return out;
}

function Field({ id, label, error, hint, children }: { id: string; label: string; error?: string | undefined; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <p className="hint">{hint}</p>}
      {error && (
        <p className="error" id={`${id}-err`}>
          {error}
        </p>
      )}
    </div>
  );
}

export function SaleForm(props: {
  /** create = public form; admin = entered by an admin (contact optional, no Turnstile) */
  mode: 'create' | 'edit' | 'admin';
  initial: SaleFormValues;
  initialNoTimes?: boolean;
  issues: Issue[];
  busy: boolean;
  onSubmit: (fields: Record<string, string>, photos: File[]) => void;
  extra?: ReactNode;
}) {
  const [v, setV] = useState<SaleFormValues>(props.initial);
  const [noTimes, setNoTimes] = useState(Boolean(props.initialNoTimes));
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoMsg, setPhotoMsg] = useState('');
  const err = (k: string) => props.issues.find((i) => i.path === k)?.message;
  const set = <K extends keyof SaleFormValues>(k: K, val: SaleFormValues[K]) => setV((s) => ({ ...s, [k]: val }));
  const text = (k: keyof SaleFormValues) => ({
    id: k,
    name: k,
    value: v[k] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => set(k, e.target.value as never),
    'aria-invalid': err(k) ? true : undefined,
    'aria-describedby': err(k) ? `${k}-err` : undefined,
  });
  const previews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const fields = toFields(v, noTimes);
        const hp = (e.currentTarget.elements.namedItem(HONEYPOT_FIELD) as HTMLInputElement | null)?.value;
        if (hp) fields[HONEYPOT_FIELD] = hp;
        props.onSubmit(fields, photos);
      }}
    >
      <Field id="event_type" label="What are you listing?" error={err('event_type')}>
        <select {...text('event_type')}>
          {Object.entries(TYPE_LABEL).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      <Field id="title" label="Title" error={err('title')} hint="e.g. “Multi-family yard sale — tools, toys, furniture”">
        <input {...text('title')} maxLength={120} required />
      </Field>
      <Field id="description" label="What's for sale or happening?" error={err('description')}>
        <textarea {...text('description')} maxLength={4000} required />
      </Field>

      <div className="form-section">
        <h2>Where</h2>
        <Field id="address_1" label="Street address" error={err('address_1')}>
          <input {...text('address_1')} autoComplete="address-line1" required />
        </Field>
        <Field id="address_2" label="Address line 2 (optional)" error={err('address_2')}>
          <input {...text('address_2')} autoComplete="address-line2" />
        </Field>
        <div className="row3">
          <Field id="city" label="City" error={err('city')}>
            <input {...text('city')} autoComplete="address-level2" required />
          </Field>
          <Field id="state" label="State" error={err('state')}>
            <input {...text('state')} autoComplete="address-level1" maxLength={2} onBlur={() => set('state', v.state.toUpperCase())} required />
          </Field>
          <Field id="postal_code" label="ZIP" error={err('postal_code')}>
            <input {...text('postal_code')} autoComplete="postal-code" inputMode="numeric" maxLength={5} required />
          </Field>
        </div>
        <Field id="venue_name" label="Place name (optional)" error={err('venue_name')} hint="Church, business, or park name, if any">
          <input {...text('venue_name')} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={v.is_online_only} onChange={(e) => set('is_online_only', e.target.checked)} />
          This is an online-only sale or auction (no in-person stop)
        </label>
        {!v.is_online_only && <HideAddressSection v={v as unknown as ScheduleValues} set={set as never} err={err} />}
      </div>

      <div className="form-section">
        <h2>When</h2>
        <div className="row">
          <Field id="start_date" label={v.repeat ? 'First date' : 'Date'} error={err('start_date')}>
            <input
              {...text('start_date')}
              type="date"
              min={today}
              onChange={(e) => setV((s) => ({ ...s, start_date: e.target.value, end_date: s.end_date && s.end_date >= e.target.value ? s.end_date : e.target.value }))}
              required
            />
          </Field>
          {!v.repeat && (
            <Field id="end_date" label="Last day" error={err('end_date')}>
              <input {...text('end_date')} type="date" min={v.start_date || today} disabled={noTimes} />
            </Field>
          )}
        </div>
        {!noTimes && (
          <div className="row">
            <Field id="start_time" label="Opens at" error={err('start_time')}>
              <input {...text('start_time')} type="time" step={900} required />
            </Field>
            <Field id="end_time" label="Closes at" error={err('end_time')}>
              <input {...text('end_time')} type="time" step={900} required />
            </Field>
          </div>
        )}
        {!v.repeat && (
          <label className="check">
            <input type="checkbox" checked={noTimes} onChange={(e) => setNoTimes(e.target.checked)} />
            Times not decided yet
          </label>
        )}
        <RepeatSection v={v as unknown as ScheduleValues} set={set as never} err={err} />
      </div>

      {v.event_type === 'auction' && (
        <div className="form-section">
          <h2>Auction details (optional)</h2>
          <Field id="auction_company" label="Auctioneer or company" error={err('auction_company')}>
            <input {...text('auction_company')} maxLength={150} />
          </Field>
          <Field
            id="auction_format"
            label="How does bidding work?"
            error={err('auction_format')}
            hint="Online-only auctions are listed, but kept off the map and out of driving routes."
          >
            <select
              {...text('auction_format')}
              onChange={(e) => {
                const format = e.target.value;
                setV((s) => ({ ...s, auction_format: format, is_online_only: format === 'online' ? true : s.is_online_only }));
              }}
            >
              <option value="">Not specified</option>
              <option value="in_person">In person</option>
              <option value="online">Online only</option>
              <option value="hybrid">Online bidding plus an in-person preview or pickup</option>
            </select>
          </Field>
          <div className="row">
            <Field id="auction_start_date" label="Bidding or auction starts" error={err('auction_start_date')}>
              <input {...text('auction_start_date')} type="date" />
            </Field>
            <Field id="auction_start_time" label="Start time" error={err('auction_start_time')}>
              <input {...text('auction_start_time')} type="time" step={900} />
            </Field>
          </div>
          <div className="row3">
            <Field id="preview_date" label="Preview date" error={err('preview_date')}>
              <input {...text('preview_date')} type="date" />
            </Field>
            <Field id="preview_start_time" label="Preview opens" error={err('preview_start_time')}>
              <input {...text('preview_start_time')} type="time" step={900} />
            </Field>
            <Field id="preview_end_time" label="Preview closes" error={err('preview_end_time')}>
              <input {...text('preview_end_time')} type="time" step={900} />
            </Field>
          </div>
          <Field id="terms_url" label="Terms and conditions link" error={err('terms_url')}>
            <input {...text('terms_url')} type="url" inputMode="url" maxLength={500} />
          </Field>
          <Field id="catalog_url" label="Catalog link" error={err('catalog_url')}>
            <input {...text('catalog_url')} type="url" inputMode="url" maxLength={500} />
          </Field>
          <Field id="pickup_info" label="Pickup information" error={err('pickup_info')} hint="When and where winning bidders collect their items">
            <input {...text('pickup_info')} maxLength={500} />
          </Field>
        </div>
      )}

      <div className="form-section">
        <h2>Extras (optional)</h2>
        {props.mode !== 'edit' && (
          <Field id="photos" label="Photos (up to 5)" error={err('photos') ?? (photoMsg || undefined)}>
            <input
              id="photos"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                const ok = files.filter((f) => f.size <= 10 * 1024 * 1024).slice(0, 5);
                setPhotoMsg(files.length > 5 ? 'Only the first 5 photos will be used' : ok.length < files.length ? 'Photos over 10 MB were skipped' : '');
                setPhotos(ok);
              }}
            />
            {previews.length > 0 && (
              <div className="photos">
                {previews.map((u, i) => (
                  <img key={u} src={u} alt={`Selected photo ${i + 1}`} />
                ))}
              </div>
            )}
          </Field>
        )}
        <Field id="special_instructions" label="Special instructions" error={err('special_instructions')} hint="Parking, early birds, cash only, vendor sign-up…">
          <input {...text('special_instructions')} maxLength={500} />
        </Field>
        <Field id="external_url" label="Website or online listing" error={err('external_url')}>
          <input {...text('external_url')} type="url" inputMode="url" placeholder="https://" />
        </Field>
      </div>

      {props.mode !== 'edit' && (
        <div className="form-section">
          <h2>{props.mode === 'admin' ? 'Host contact (private, optional)' : 'Your contact info (private)'}</h2>
          <p className="hint">
            {props.mode === 'admin'
              ? "Fill in whatever the host sent. If there's an email, they'll get the confirmation and private edit link."
              : 'Your contact information is required to submit a listing but will not be displayed publicly.'}
          </p>
          <Field id="contact_name" label={props.mode === 'admin' ? 'Host name' : 'Your name'} error={err('contact_name')}>
            <input {...text('contact_name')} autoComplete="name" required />
          </Field>
          <div className="row">
            <Field id="contact_phone" label="Phone" error={err('contact_phone')}>
              <input {...text('contact_phone')} type="tel" autoComplete="tel" required />
            </Field>
            <Field id="contact_email" label="Email" error={err('contact_email')}>
              <input {...text('contact_email')} type="email" autoComplete="email" required />
            </Field>
          </div>
          {props.mode === 'create' && (
            <label className="check">
              <input type="checkbox" name="authorized" checked={v.authorized} onChange={(e) => set('authorized', e.target.checked)} />
              I confirm that I am authorized to submit this sale/event and that the information provided is accurate.
            </label>
          )}
          {err('authorized') && <p className="error">{err('authorized')}</p>}
          {photos.length > 0 && (
            <label className="check">
              <input type="checkbox" name="photo_rights" checked={v.photo_rights} onChange={(e) => set('photo_rights', e.target.checked)} />
              {props.mode === 'admin'
                ? 'The host has the right to share these photos and sent them for this listing.'
                : "I confirm I have the right to share the photo(s) I'm uploading."}
            </label>
          )}
          {err('photo_rights') && <p className="error">{err('photo_rights')}</p>}
          {props.mode === 'create' && (
            <>
              <label className="check">
                <input type="checkbox" name="newsletter_opt_in" checked={v.newsletter_opt_in} onChange={(e) => set('newsletter_opt_in', e.target.checked)} />
                Send me the free weekly Yard Sale Map email for the areas I choose.
              </label>
              {/* honeypot — hidden from people, irresistible to bots */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="fax-hp">Fax number</label>
                <input id="fax-hp" name={HONEYPOT_FIELD} tabIndex={-1} autoComplete="off" />
              </div>
            </>
          )}
        </div>
      )}

      {props.extra}

      {props.mode === 'create' && <p className="info">{CUTOFF_COPY}</p>}
      <p className="error" role="alert" aria-live="assertive">
        {props.issues.length ? 'Please fix the highlighted fields.' : ''}
      </p>
      <button className="primary" type="submit" disabled={props.busy} style={{ width: '100%', fontSize: '1.1rem' }}>
        {props.busy ? 'Sending…' : props.mode === 'create' ? 'List my sale — free' : props.mode === 'admin' ? 'Add this sale' : 'Save changes'}
      </button>
    </form>
  );
}
